// src/app/pages/accounting/chart-of-accounts/components/AccountGroupForm.tsx
import React, { useEffect } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from 'lucide-react';
import { AccountGroup, AccountGroupCreate, AccountGroupUpdate, AccountGroupResponse, AccountTypeResponse } from '@/app/types';

// Zod Schema for Account Group Form
const formSchema = z.object({
  name: z.string().min(1, 'Group name is required').max(100),
  code: z.string().max(50)
    .refine(s => !s || !/\s/.test(s), 'Code cannot contain spaces')
    .optional()
    .or(z.literal('')),
  account_type_id: z.number({ required_error: "Account type is required" }).min(1, "Account type is required"), // Ensure a valid ID > 0
  parent_id: z.number().nullable().optional(), // This should be null for root
  description: z.string().optional().nullable(),
  is_active: z.boolean().default(true), // Assuming is_active is part of the form, based on previous context
});

type FormValues = z.infer<typeof formSchema>;

interface AccountGroupFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: AccountGroupCreate | AccountGroupUpdate) => void;
  defaultValues?: Partial<AccountGroup>;
  isLoading?: boolean;
  mode: 'create' | 'edit';
  accountTypes: AccountTypeResponse[];
  accountGroups: AccountGroupResponse[]; // All groups for parent selection
  currentAccountGroupId?: number; // Used when editing to prevent selecting self or descendants as parent
}

const AccountGroupForm: React.FC<AccountGroupFormProps> = ({
  open,
  onOpenChange,
  onSubmit,
  defaultValues,
  isLoading = false,
  mode,
  accountTypes,
  accountGroups,
  currentAccountGroupId,
}) => {
  // Initialize the form with defaultValues or empty values
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      code: '',
      account_type_id: 0,
      parent_id: null,
      description: '',
      is_active: true,
    },
  });

  // Reset form when dialog opens with new data
  useEffect(() => {
    if (open) {
      if (defaultValues) {
        form.reset({
          name: defaultValues.name || '',
          code: defaultValues.code || '',
          account_type_id: defaultValues.account_type_id || 0,
          parent_id: defaultValues.parent_id === 0 ? null : defaultValues.parent_id ?? null,
          description: defaultValues.description || '',
          is_active: defaultValues.is_active ?? true,
        });
      } else {
        form.reset({
          name: '',
          code: '',
          account_type_id: 0,
          parent_id: null,
          description: '',
          is_active: true,
        });
      }
    }
  }, [open, defaultValues, form]);

  // Helper to check if an account group is a descendant of the current group being edited
  const isDescendantOf = (groupId: number, potentialAncestorId: number): boolean => {
    if (mode !== 'edit' || !potentialAncestorId) return false;
    if (groupId === potentialAncestorId) return true; // Cannot select self

    const group = accountGroups.find(g => g.id === groupId);
    if (!group || !group.parent_id) return false; // Not found or is a root group
    if (group.parent_id === potentialAncestorId) return true; // Direct child

    // Recursively check the parent
    return isDescendantOf(group.parent_id, potentialAncestorId);
  };

  // Filter account groups to exclude self and descendants for the parent dropdown
  const filteredAccountGroups = accountGroups.filter(
    group => !currentAccountGroupId || !isDescendantOf(group.id, currentAccountGroupId)
  );

  // Handle form submission
  const handleSubmit: SubmitHandler<FormValues> = (values: FormValues): void => {
    // Construct the payload ensuring parent_id is null if it was set to null in the form state
    const payload: AccountGroupCreate | AccountGroupUpdate = {
      ...values,
      parent_id: values.parent_id, // Already handled by onChange, should be null if root was selected
    };
    // Optionally: If you strictly want to omit the key when null
    // const payload = { ...values };
    // if (payload.parent_id === null) {
    //   delete payload.parent_id;
    // }
    console.log("Submitting payload:", payload); // Log the payload being sent
    onSubmit(payload);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'Create New Account Group' : 'Edit Account Group'}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4 py-2">
            {/* Name and Code */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Group Name*</FormLabel>
                    <FormControl>
                      <Input {...field} autoFocus />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Group Code</FormLabel>
                    <FormControl>
                      <Input {...field} value={field.value || ''} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Account Type and Parent Group */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="account_type_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Account Type*</FormLabel>
                    <Select
                      onValueChange={(value) => field.onChange(value ? parseInt(value) : 0)}
                      value={field.value ? field.value.toString() : undefined}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select Account Type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {accountTypes.map((type) => (
                          <SelectItem key={type.id} value={type.id.toString()}>
                            {type.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="parent_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Parent Group (Optional)</FormLabel>
                    <Select
                      // Use 'null_value' string to represent null in the select options
                      value={field.value === null ? 'null_value' : field.value?.toString() || undefined}
                      onValueChange={(value) => field.onChange(value === 'null_value' ? null : parseInt(value))}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select Parent Group" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {/* Use a distinct value like 'null_value' for the null option */}
                        <SelectItem value="null_value">None (Root Group)</SelectItem>
                        {filteredAccountGroups.map((group) => (
                          <SelectItem key={group.id} value={group.id.toString()}>
                            {group.name} ({group.code})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Description */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      value={field.value || ''}
                      placeholder="Group description (optional)"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Is Active Checkbox */}
            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4 mt-6">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Active</FormLabel>
                    <FormDescription>
                      Inactive groups won&apos;t appear in dropdown menus for new accounts.
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />

            {/* Dialog Footer */}
            <DialogFooter>
              <Button
                type="button"
                variant="secondary"
                onClick={() => onOpenChange(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default AccountGroupForm;